
import React from 'react';
import { useLanguage } from "@/hooks/useLanguage";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield } from "lucide-react";
import { VulnerabilityItem } from '@/types/dashboard';
import { VulnerabilityListItem } from "./VulnerabilityListItem";
import { Link } from 'react-router-dom';

interface VulnerabilitiesCardProps {
  vulnerabilities: VulnerabilityItem[];
  isLoading: boolean;
}

export function VulnerabilitiesCard({ vulnerabilities, isLoading }: VulnerabilitiesCardProps) {
  const { t } = useLanguage();

  return (
    <Card className="border-border/50 h-full bg-card/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300 hover:border-destructive/50 group">
      <CardHeader className="pb-2">
        <CardTitle className="text-xl flex items-center">
          <div className="p-2 rounded-lg bg-destructive/10 mr-3 group-hover:bg-destructive/20 transition-colors">
            <Shield className="h-5 w-5 text-destructive" />
          </div>
          <Link to="/cve" className="hover:text-destructive transition-colors flex-1">
            {t('dashboard.critical_vulnerabilities_recent')}
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex flex-col gap-2">
                <div className="h-5 w-3/4 bg-secondary/50 animate-pulse rounded-md" />
                <div className="h-4 w-1/4 bg-secondary/30 animate-pulse rounded-md" />
              </div>
            ))}
          </div>
        ) : vulnerabilities && vulnerabilities.length > 0 ? (
          <div className="space-y-2">
            {vulnerabilities.slice(0, 5).map((vulnerability, index) => (
              <Link
                key={index}
                to={`/cve/${vulnerability.id}`}
                state={{
                  description_en: vulnerability.description,
                  description_pl: vulnerability.description_pl
                }}
                className="block hover:bg-secondary/50 rounded-lg transition-colors"
              >
                <VulnerabilityListItem vulnerability={vulnerability} />
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">{t('common.no_results')}</p>
        )}
      </CardContent>
    </Card>
  );
}
